import * as am5 from '@amcharts/amcharts5';
import * as am5core from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import { XYChart } from '@amcharts/amcharts5/xy';
import { ChartViewOptions } from '../components/slices/AssetTrendDataSlice';
import { ChartConfig, generateLineType, generateMarkers } from './chartUtils';
import {
  createTooltip,
  customizeAxisLabels,
  setupCursor,
  createCursorBullet,
  configureXAxisRendererTicks,
  prepareChartContainer,
  setupCursorEvents,
} from './chartHelpers';

interface DataPoint {
  date: number;
  value: number;
  index: number;
  cov: number;
  cumulativeCOV: number;
}

export const createTimeSeriesChart = (
  container: HTMLElement,
  data: DataPoint[],
  chartConfig: ChartConfig,
  title: string,
  shortUnitOfMeasure: string,
  addAmChartRef: (index: number, chart: XYChart, yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>, id: string) => void,
  chartIndex: number,
  syncCursor: () => void,
  syncAxes: (chart: any) => void,
  chartViewOptions: ChartViewOptions,
  startDate?: Date,
  endDate?: Date,
) => {
  if (!prepareChartContainer(container)) {
    return;
  }

  // Create root element
  const root = am5.Root.new(container);
  const { chartColor, markerColor, markerType, lineType, lineWeight, markerSize } = chartConfig;
  root.setThemes([am5themes_Dark.new(root)]);

  const { chartType, showTooltip } = chartViewOptions;

  // Create chart
  const chart = root.container.children.push(
    am5xy.XYChart.new(root, {
      panX: false,
      panY: false,
      wheelX: 'none',
      wheelY: 'none',
      paddingLeft: 0,
      paddingRight: 30,
    }),
  );

  // Create axes
  const xAxis = chart.xAxes.push(
    am5xy.DateAxis.new(root, {
      maxDeviation: 0.2,
      baseInterval: {
        timeUnit: 'second',
        count: 1,
      },
      renderer: am5xy.AxisRendererX.new(root, {
        strokeWidth: 1,
        strokeOpacity: 1,
        stroke: am5core.color('#4A5463'),
        minGridDistance: 80,
        minorGridEnabled: true,
        pan: 'zoom',
      }),
    }),
  );

  if (startDate && endDate) {
    xAxis.setAll({
      min: new Date(startDate).getTime(),
      max: new Date(endDate).getTime(),
      strictMinMax: true,
    });
  }

  const xAxisRenderer = xAxis.get('renderer') as am5xy.AxisRendererX;
  configureXAxisRendererTicks(xAxisRenderer);
  xAxisRenderer.grid.template.setAll({ strokeOpacity: 0 });

  const yAxis = chart.yAxes.push(
    am5xy.ValueAxis.new(root, {
      maxDeviation: 0.2,
      renderer: am5xy.AxisRendererY.new(root, {
        pan: 'zoom',
        strokeWidth: 1,
        strokeOpacity: 1,
        stroke: am5core.color('#4A5463'),
      }),
    }),
  );
  yAxis.children.unshift(
    am5.Label.new(root, {
      rotation: -90,
      text: shortUnitOfMeasure,
      y: am5.p50,
      centerX: am5.p50,
      fill: am5.color('#f7f9f9'),
      fontSize: 12,
      fontFamily: 'Mulish',
      fontWeight: '400',
    }),
  );

  yAxis.children.unshift(
    am5.Label.new(root, {
      rotation: -90,
      text: title,
      y: am5.p50,
      centerX: am5.p50,
      fill: am5.color('#f7f9f9'),
      fontSize: 16,
      fontFamily: 'Mulish',
      fontWeight: '400',
      marginRight: -10,
      textAlign: 'center',
      maxHeight: 180,
      oversizedBehavior: 'wrap',
    }),
  );

  const yRenderer = yAxis.get('renderer');
  yRenderer.grid.template.setAll({ strokeOpacity: 0 });
  customizeAxisLabels(yRenderer, 12);

  // Right Y axis for COV and cumulativeCOV
  const yAxisRight = chart.yAxes.push(
    am5xy.ValueAxis.new(root, {
      maxDeviation: 0.2,
      renderer: am5xy.AxisRendererY.new(root, {
        pan: 'zoom',
        opposite: true,
        strokeWidth: 1,
        strokeOpacity: 1,
        stroke: am5core.color('#4A5463'),
      }),
    }),
  );
  yAxisRight.children.unshift(
    am5.Label.new(root, {
      rotation: -90,
      y: am5.p50,
      centerX: am5.p50,
      fill: am5.color('#f7f9f9'),
      fontSize: 12,
      fontFamily: 'Mulish',
      fontWeight: '400',
    }),
  );

  const rightYRenderer = yAxisRight.get('renderer');
  rightYRenderer.grid.template.setAll({ strokeOpacity: 0 });

  const tooltip = createTooltip('', root, {
    backgroundColor: '#001023',
    backgroundOpacity: 1,
    strokeColor: '#001023',
    textColor: '#FFFFFF',
    fontSize: 14,
    verticalPosition: 'center',
    chartHeight: chart.plotContainer.height(),
  });

  // --- Series and Bullets ---
  const bullets: am5.Bullet[] = [];
  const cursorBullets: { [key: string]: am5.Bullet } = {};

  // Main value series
  const series = chart.series.push(
    am5xy.LineSeries.new(root, {
      name: chartType === 'scatter' ? 'Scatter View' : 'Line View',
      xAxis: xAxis,
      yAxis: yAxis,
      valueYField: 'value',
      valueXField: 'date',
      tooltip: showTooltip ? tooltip : undefined,
    }),
  );
  series.set('stroke', am5.color(chartColor));
  generateLineType(series, lineType, lineWeight);

  // Cursor bullet for value series
  const valueCursorBullet = createCursorBullet(root, chartType === 'scatter' ? markerColor : chartColor);
  series.bulletsContainer.children.push(valueCursorBullet.get('sprite'));
  bullets.push(valueCursorBullet);
  cursorBullets['value'] = valueCursorBullet;

  if (chartType === 'scatter') {
    series.strokes.template.setAll({ strokeOpacity: 0 });
    series.bullets.push(function () {
      return am5.Bullet.new(root, {
        sprite: generateMarkers(markerType, markerColor, markerSize, root),
      });
    });
  }
  series.data.setAll(data);

  // COV series (right axis)
  const filteredCovData = (data || []).filter((point: any) => typeof point.cov === 'number' && !isNaN(point.cov));
  const covSeries = chart.series.push(
    am5xy.LineSeries.new(root, {
      name: 'COV',
      xAxis: xAxis,
      yAxis: yAxisRight,
      valueYField: 'cov',
      valueXField: 'date',
    }),
  );
  covSeries.set('stroke', am5.color('#E94E89'));

  const covCursorBullet = createCursorBullet(root, '#E94E89');
  covSeries.bulletsContainer.children.push(covCursorBullet.get('sprite'));
  bullets.push(covCursorBullet);
  cursorBullets['cov'] = covCursorBullet;

  if (chartType === 'scatter') {
    covSeries.strokes.template.setAll({ strokeOpacity: 0 });
    covSeries.bullets.push(function () {
      return am5.Bullet.new(root, {
        sprite: generateMarkers(markerType, '#E94E89', markerSize, root),
      });
    });
  }
  covSeries.data.setAll(filteredCovData);

  // cumulativeCOV series (right axis)
  const filteredCumulativeCovData = (data || []).filter(
    (point: any) => typeof point.cumulativeCOV === 'number' && !isNaN(point.cumulativeCOV),
  );
  const cumulativeCovSeries = chart.series.push(
    am5xy.LineSeries.new(root, {
      name: 'Cumulative COV',
      xAxis: xAxis,
      yAxis: yAxisRight,
      valueYField: 'cumulativeCOV',
      valueXField: 'date',
      tooltip: showTooltip ? tooltip : undefined,
    }),
  );
  cumulativeCovSeries.set('stroke', am5.color('#35BB21'));
  generateLineType(cumulativeCovSeries, lineType, lineWeight);

  const cumulativeCovCursorBullet = createCursorBullet(root, '#35BB21');
  cumulativeCovSeries.bulletsContainer.children.push(cumulativeCovCursorBullet.get('sprite'));
  bullets.push(cumulativeCovCursorBullet);
  cursorBullets['cumulativeCOV'] = cumulativeCovCursorBullet;

  if (chartType === 'scatter') {
    cumulativeCovSeries.strokes.template.setAll({ strokeOpacity: 0 });
    cumulativeCovSeries.bullets.push(function () {
      return am5.Bullet.new(root, {
        sprite: generateMarkers(markerType, '#35BB21', markerSize, root),
      });
    });
  }
  cumulativeCovSeries.data.setAll(filteredCumulativeCovData);

  //Cursor and Events
  const cursor = setupCursor(chart, root, xAxis, '#60BFDA', 2);

  setupCursorEvents(
    cursor,
    xAxis,
    chart,
    [series, covSeries, cumulativeCovSeries],
    [valueCursorBullet, covCursorBullet, cumulativeCovCursorBullet],
    [],
    [],
    chartType,
    {
      customTooltip: tooltip,
      title: title,
      shortUnitOfMeasure: shortUnitOfMeasure,
      showTooltip: showTooltip,
      syncCursor: syncCursor,
      chartConfig: chartConfig,
    },
  );

  cursor.events.on('cursorhidden', () => {
    tooltip.hide();
    Object.values(cursorBullets).forEach((bullet) => bullet.get('sprite').set('visible', false));
  });

  xAxis.on('start', function () {
    syncAxes(chart);
  });
  xAxis.on('end', function () {
    syncAxes(chart);
  });

  addAmChartRef(chartIndex, chart, yAxis, title);
  return root;
};
